import { Link } from "react-router-dom";
import { useMemo } from "react";

/**
 * RoomItem — dispatches to a per-metaphor render:
 *   vhs      → VHS case (title bar underneath)
 *   spine    → vertical book spine on a shelf
 *   record   → cover + vinyl peeking out
 *   card     → article index card w/ clipped tab
 *   recipe   → recipe index card (warmer paper)
 *   polaroid → photo polaroid w/ caption
 * All render as `<Link to="/item/:id">` so tapping opens ItemDetail.
 */
export default function RoomItem({ item, metaphor }) {
  const to = `/item/${item.id}`;
  const title = item.title || "Untitled";
  const creator = item.creator || "";
  const cover = item.image_url || "";

  if (metaphor === "spine") return <BookSpine to={to} title={title} creator={creator} id={item.id} />;
  if (metaphor === "record") return <RecordSleeve to={to} title={title} artist={creator} cover={cover} />;
  if (metaphor === "polaroid") return <Polaroid to={to} title={title} cover={cover} />;
  if (metaphor === "card" || metaphor === "recipe") {
    return <IndexCardItem to={to} title={title} source={creator} kind={metaphor} />;
  }
  // default: vhs (movie, tvshow, video)
  return <VhsItem to={to} title={title} creator={creator} cover={cover} />;
}

function VhsItem({ to, title, creator, cover }) {
  return (
    <Link to={to} className="vhs-case" data-testid={`item-card-${to.split('/').pop()}`}>
      <div className="vhs-case-art" style={cover ? { backgroundImage: `url(${cover})` } : undefined} />
      <div className="vhs-case-label">
        <span className="vhs-title">{title}</span>
        {creator && <span className="vhs-meta">{creator}</span>}
      </div>
    </Link>
  );
}

// Deterministic spine width & color from item id, so the shelf looks varied
// but stable across renders (no "flicker on refresh").
const SPINE_COLORS = ["#8C3A3A", "#3C5A6E", "#5A6E3A", "#8E6A2A", "#3A4A6E", "#5B3A5C", "#6B2A3A", "#4E6B4A"];

function hashCode(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function BookSpine({ to, title, creator, id }) {
  const { width, height, color } = useMemo(() => {
    const h = hashCode(id || title);
    return {
      width: 28 + (h % 22),          // 28..49px
      height: 190 + ((h >> 3) % 40), // 190..229px
      color: SPINE_COLORS[h % SPINE_COLORS.length],
    };
  }, [id, title]);
  return (
    <Link
      to={to}
      className="book-spine"
      style={{ "--spine-w": `${width}px`, "--spine-h": `${height}px`, "--spine-color": color }}
      data-testid={`item-card-${id}`}
      title={creator ? `${title} — ${creator}` : title}
    >
      <span className="book-spine-title">{title}</span>
    </Link>
  );
}

function RecordSleeve({ to, title, artist, cover }) {
  return (
    <Link to={to} className="record-sleeve" data-testid={`item-card-${to.split('/').pop()}`}>
      <div className="record-vinyl" />
      <div className="record-sleeve-cover" style={cover ? { backgroundImage: `url(${cover})` } : undefined} />
      <div className="record-sleeve-meta">
        <p className="record-sleeve-title">{title}</p>
        {artist && <p className="record-sleeve-artist">{artist}</p>}
      </div>
    </Link>
  );
}

function IndexCardItem({ to, title, source, kind }) {
  return (
    <Link to={to} className={`index-card ${kind === "recipe" ? "recipe-card" : ""}`} data-testid={`item-card-${to.split('/').pop()}`}>
      <h3 className="index-card-title">{title}</h3>
      {source && <p className="index-card-source">{source}</p>}
    </Link>
  );
}

function Polaroid({ to, title, cover }) {
  return (
    <Link to={to} className="polaroid" data-testid={`item-card-${to.split('/').pop()}`}>
      {cover
        ? <img src={cover} alt="" loading="lazy" />
        : <div style={{ width: "100%", aspectRatio: "1 / 1", background: "#241F1B" }} />}
      <p className="polaroid-cap">{title}</p>
    </Link>
  );
}
